export type TranslationCountryName = {
  languageCode: string
  name: string
}

export type TranslationCountryRow = {
  code: number
  alpha2: string
  alpha3: string
  name: string
  isSupported: boolean
  isActive: boolean
  hasPolygon: boolean
  // Every name the catalog carries, including languages that are no longer
  // supported — the editor shows them all, because a save replaces the map.
  names: TranslationCountryName[]
  missingLanguages: string[]
  supportedLanguageCount: number
  includedFeatureCount: number
  excludedFeatureCount: number
}

/** Supported languages this country already has a name in. */
export function namedLanguageCount(row: TranslationCountryRow) {
  return row.supportedLanguageCount - row.missingLanguages.length
}

/**
 * Whether the country has everything a market needs, for the table's at-a-
 * glance column.
 *
 * Geometry only, deliberately. A missing name is NOT a readiness problem:
 * every one of the 250 countries carries names in three languages and the
 * catalog supports five, so counting that as unready would flag the three
 * live markets — which ship with exactly that gap and render fine, because
 * the console falls back to English. A column that calls Norway unready
 * teaches operators to ignore it. Name coverage gets its own column, where
 * it reads as a number to improve rather than a problem to fix.
 *
 * A missing polygon is different: nothing falls back for it, so the map has
 * no extent to open the country at.
 */
export function countryReadiness(row: TranslationCountryRow) {
  return row.hasPolygon
    ? { isReady: true, label: "Ready" }
    : { isReady: false, label: "No map geometry" }
}

/**
 * The name gap spelled out, for the places with room for a sentence (the
 * support confirmation). Null when the country is named in every supported
 * language.
 */
export function countryNameGap(row: TranslationCountryRow) {
  if (row.missingLanguages.length === 0) return null
  return `named in ${namedLanguageCount(row)} of ${row.supportedLanguageCount} languages (no ${row.missingLanguages.join(", ")})`
}
